import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ChevronLeft, Sparkles, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { useDebounce } from "@/react/hooks/useDebounce";

import {
  NOTIFICATION_MARK_ALL_ACK_MS,
  NOTIFICATION_POPUP_DEBOUNCE_MS,
  NOTIFICATION_POPUP_EXPANDED_SIZE,
  NOTIFICATION_POPUP_SHELL_MORPH_MS,
  NOTIFICATION_POPUP_SUMMARY_SIZE,
  ROUTINE_SHELL_EXPANDED_AFTER_CATEGORY_COUNT,
  SURFACE_ICON_BTN_CLS,
  SURFACE_ICON_GLYPH_CLS,
} from "../constants/layout";
import { getNotificationCategory } from "../constants/config";
import { useNotificationPopup } from "../hooks/useNotificationPopup";
import type {
  ChatMessage,
} from "@/modern/components/chatbox/types";
import type {
  NotificationFilter,
  NotificationSurfaceMode,
} from "../types";
import { NotificationCategoryView } from "./NotificationCategoryView";
import { NotificationAskView } from "./NotificationAskView";
import {
  NotificationCategoriesView,
  visibleRoutineCategories,
} from "./NotificationCategoriesView";
import { NotificationNotableView } from "./NotificationNotableView";
import { NotificationHeaderListChrome } from "./NotificationSearchField";
import { NotificationSummaryView } from "./NotificationSummaryView";
import {
  askSurfaceCaption,
  type UpdatesAskOrigin,
} from "../utils/askUiContext";
import {
  DEFAULT_UPDATES_HISTORY_RANGE,
  DEFAULT_UPDATES_INBOX_MODE,
  type UpdatesHistoryRange,
  type UpdatesInboxMode,
} from "../utils/timeWindow";
import { NotificationWindowChrome } from "./NotificationTimeWindowControl";

const CONTENT_FADE_MS = 140;

type ShellKind = "summary" | "expanded";

const SHELL_SIZE = {
  summary: NOTIFICATION_POPUP_SUMMARY_SIZE,
  expanded: NOTIFICATION_POPUP_EXPANDED_SIZE,
} as const;

const SHELL_RANK: Record<ShellKind, number> = {
  summary: 0,
  expanded: 1,
};

function shellKindFor(
  mode: NotificationSurfaceMode["mode"],
  routineCategoryCount = 0,
): ShellKind {
  if (mode === "notable" || mode === "category" || mode === "ask") return "expanded";
  if (
    mode === "categories"
    && routineCategoryCount > ROUTINE_SHELL_EXPANDED_AFTER_CATEGORY_COUNT
  ) {
    return "expanded";
  }
  return "summary";
}

function surfaceLabelForMode(mode: NotificationSurfaceMode): string {
  if (mode.mode === "summary") return "Updates";
  if (mode.mode === "notable") return "Notable";
  if (mode.mode === "categories") return "Routine";
  if (mode.mode === "ask") return "Ask Swivu";
  if (mode.mode === "category") {
    return (
      mode.label
      ?? getNotificationCategory(mode.filter.category)?.label
      ?? mode.filter.category
    );
  }
  return "Updates";
}

interface NotificationPopupProps {
  record?: unknown;
  onClose: () => void;
  editorInstance?: unknown;
  isShow?: boolean;
}

export function NotificationPopup({
  onClose,
  isShow = true,
}: NotificationPopupProps) {
  if (!isShow) return null;
  return <NotificationPopupShell onClose={onClose} />;
}

function NotificationPopupShell({ onClose }: { onClose: () => void }) {
  const [view, setView] = useState<NotificationSurfaceMode>({ mode: "summary" });
  const [shellKind, setShellKind] = useState<ShellKind>("summary");
  const [contentVisible, setContentVisible] = useState(true);
  const morphTimersRef = useRef<number[]>([]);
  const surfaceHistoryRef = useRef<NotificationSurfaceMode[]>([]);
  const viewRef = useRef(view);
  viewRef.current = view;
  /** Wait for first list success before fading category/Notable detail in. */
  const pendingListRevealRef = useRef(false);
  const [filter, setFilter] = useState<NotificationFilter>({ kind: "none" });
  const [inboxMode, setInboxMode] = useState<UpdatesInboxMode>(
    DEFAULT_UPDATES_INBOX_MODE,
  );
  const [historyRange, setHistoryRange] = useState<UpdatesHistoryRange>(
    DEFAULT_UPDATES_HISTORY_RANGE,
  );
  const openHistory = useCallback(() => {
    setInboxMode("all");
  }, []);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, NOTIFICATION_POPUP_DEBOUNCE_MS) as string;
  const [askOrigin, setAskOrigin] = useState<UpdatesAskOrigin>({ kind: "summary" });
  const [askMessages, setAskMessages] = useState<ChatMessage[]>([]);
  const [askSessionId, setAskSessionId] = useState<string | undefined>(undefined);
  const [askIsLoading, setAskIsLoading] = useState(false);
  const [markAllAck, setMarkAllAck] = useState<{
    markedCount: number;
    nextSurfaceLabel: string;
  } | null>(null);
  const markAllAckTimerRef = useRef<number | null>(null);

  const isSummary = view.mode === "summary";
  const isMarkAllAck = markAllAck != null;

  const activeFilter: NotificationFilter =
    view.mode === "category" ? view.filter
    : view.mode === "notable" ? { kind: "notable" }
    : filter;

  const popup = useNotificationPopup({
    filter: activeFilter,
    searchQuery: view.mode === "category" || view.mode === "notable" ? debouncedSearch : "",
    // Prefetch category lists while the Routine→detail shell morphs (filter is
    // set before `view` flips to `category`).
    listEnabled:
      view.mode === "category" ||
      view.mode === "notable" ||
      (view.mode === "categories" && filter.kind === "category"),
    closePopup: onClose,
    inboxMode,
    historyRange,
  });

  const routineCategoryCount = useMemo(
    () => visibleRoutineCategories(popup.mappedRoutineCounts).length,
    [popup.mappedRoutineCounts],
  );

  // Unseen only — drives whether Mark all is shown (hidden when nothing to clear).
  const markableCount =
    view.mode === "notable"
      ? popup.mappedNotableCounts.unread
      : view.mode === "category"
        ? inboxMode === "unread"
          // Unread list query is already is_seen:false, so category totals = unseen.
          ? (popup.mappedRoutineCounts.byCategory[view.filter.category] ?? 0)
          // All: category_counts include seen — count unseen in the loaded list.
          : popup.notifications.reduce(
              (n, item) => n + (item.is_seen ? 0 : 1),
              0,
            )
        : 0;
  const routineCategoryCountRef = useRef(routineCategoryCount);
  routineCategoryCountRef.current = routineCategoryCount;
  const shellKindRef = useRef(shellKind);
  shellKindRef.current = shellKind;

  const clearMorphTimers = useCallback(() => {
    for (const id of morphTimersRef.current) window.clearTimeout(id);
    morphTimersRef.current = [];
  }, []);

  const schedule = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    morphTimersRef.current.push(id);
  }, []);

  useEffect(() => () => clearMorphTimers(), [clearMorphTimers]);

  const clearMarkAllAckTimer = useCallback(() => {
    if (markAllAckTimerRef.current != null) {
      window.clearTimeout(markAllAckTimerRef.current);
      markAllAckTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearMarkAllAckTimer(), [clearMarkAllAckTimer]);

  // Reveal list detail only after the first fetch settles (or timeout) so we
  // never flash CaughtUp/skeleton mid-morph on first open.
  useEffect(() => {
    if (!pendingListRevealRef.current) return;
    if (view.mode !== "category" && view.mode !== "notable") {
      pendingListRevealRef.current = false;
      setContentVisible(true);
      return;
    }
    if (isMarkAllAck || !popup.isListPending) {
      pendingListRevealRef.current = false;
      setContentVisible(true);
    }
  }, [isMarkAllAck, popup.isListPending, view.mode]);

  useEffect(() => {
    if (!pendingListRevealRef.current) return;
    if (view.mode !== "category" && view.mode !== "notable") return;
    const id = window.setTimeout(() => {
      if (!pendingListRevealRef.current) return;
      pendingListRevealRef.current = false;
      setContentVisible(true);
    }, 2500);
    return () => window.clearTimeout(id);
  }, [view.mode]);

  const prefersReducedMotion = useCallback(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  /** Same fade → resize → reveal timing as surface navigation (size-only). */
  const morphShellTo = useCallback(
    (toKind: ShellKind) => {
      const fromKind = shellKindRef.current;
      if (fromKind === toKind) return;

      if (prefersReducedMotion()) {
        clearMorphTimers();
        setShellKind(toKind);
        setContentVisible(true);
        return;
      }

      clearMorphTimers();
      setContentVisible(false);

      schedule(() => {
        setShellKind(toKind);
        schedule(() => {
          setContentVisible(true);
        }, NOTIFICATION_POPUP_SHELL_MORPH_MS);
      }, CONTENT_FADE_MS);
    },
    [clearMorphTimers, prefersReducedMotion, schedule],
  );

  // Unread ↔ All can change Routine tile count without a surface navigate —
  // morph shell size with the same choreography as navigation.
  useEffect(() => {
    if (view.mode !== "categories") return;
    morphShellTo(shellKindFor("categories", routineCategoryCount));
  }, [view.mode, routineCategoryCount, morphShellTo]);

  /** Morph shell size, then swap surface — avoids content jumping mid-resize. */
  const navigateSurface = useCallback(
    (
      next: NotificationSurfaceMode,
      options?: { pushHistory?: boolean },
    ) => {
      const current = viewRef.current;
      const pushHistory = options?.pushHistory !== false;
      if (pushHistory && next.mode !== current.mode) {
        surfaceHistoryRef.current.push(current);
      }

      const fromKind = shellKindFor(
        current.mode,
        routineCategoryCountRef.current,
      );
      const toKind = shellKindFor(next.mode, routineCategoryCountRef.current);

      if (fromKind === toKind && next.mode === current.mode) {
        setView(next);
        return;
      }

      if (prefersReducedMotion()) {
        clearMorphTimers();
        setView(next);
        setShellKind(toKind);
        setContentVisible(true);
        return;
      }

      clearMorphTimers();
      setContentVisible(false);

      const fromRank = SHELL_RANK[fromKind];
      const toRank = SHELL_RANK[toKind];

      if (toRank > fromRank) {
        schedule(() => {
          setShellKind(toKind);
          schedule(() => {
            // Arm reveal gate before flipping the view so the effect never races
            // a commit where pending is still false.
            if (next.mode === "category" || next.mode === "notable") {
              pendingListRevealRef.current = true;
            }
            setView(next);
            if (next.mode !== "category" && next.mode !== "notable") {
              setContentVisible(true);
            }
          }, NOTIFICATION_POPUP_SHELL_MORPH_MS);
        }, CONTENT_FADE_MS);
        return;
      }

      if (toRank < fromRank) {
        schedule(() => {
          setView(next);
          setShellKind(toKind);
          schedule(() => {
            setContentVisible(true);
          }, NOTIFICATION_POPUP_SHELL_MORPH_MS);
        }, CONTENT_FADE_MS);
        return;
      }

      schedule(() => {
        setView(next);
        setContentVisible(true);
      }, CONTENT_FADE_MS);
    },
    [clearMorphTimers, prefersReducedMotion, schedule],
  );

  const goBack = useCallback(() => {
    clearMarkAllAckTimer();
    setMarkAllAck(null);
    const previous =
      surfaceHistoryRef.current.pop() ?? ({ mode: "summary" } as const);
    setSearchInput("");
    if (previous.mode === "summary") {
      setFilter({ kind: "none" });
      surfaceHistoryRef.current = [];
    } else if (previous.mode === "categories") {
      // Stop category-list prefetch while browsing Routine.
      setFilter({ kind: "none" });
    } else if (previous.mode === "category") {
      setFilter(previous.filter);
    }
    navigateSurface(previous, { pushHistory: false });
  }, [clearMarkAllAckTimer, navigateSurface]);

  const handleMarkAllSeen = useCallback(async () => {
    if (isMarkAllAck || popup.isMarkingAll) return;

    // All = history window: mark unseen, stay on the surface so remaining
    // (already-seen) rows keep showing. Unread = catch-up: ack empty then back.
    if (inboxMode === "all") {
      try {
        await popup.markAllSeen();
      } catch {
        // Mutation rolls back optimistic cache; stay on the surface.
      }
      return;
    }

    const previous =
      surfaceHistoryRef.current[surfaceHistoryRef.current.length - 1]
      ?? ({ mode: "summary" } as const);
    const nextSurfaceLabel = surfaceLabelForMode(previous);
    const provisionalCount = popup.notifications.length;

    // Show the same CaughtUp tile immediately (forced empty) so mosaic tiles
    // never clear into a second empty chrome during the mutation/refetch.
    setSearchInput("");
    pendingListRevealRef.current = false;
    setContentVisible(true);
    setMarkAllAck({
      markedCount: provisionalCount,
      nextSurfaceLabel,
    });
    clearMarkAllAckTimer();
    markAllAckTimerRef.current = window.setTimeout(() => {
      markAllAckTimerRef.current = null;
      goBack();
    }, NOTIFICATION_MARK_ALL_ACK_MS);

    try {
      const result = await popup.markAllSeen();
      setMarkAllAck((prev) =>
        prev
          ? {
              ...prev,
              markedCount: result.updated_count ?? prev.markedCount,
            }
          : prev,
      );
    } catch {
      clearMarkAllAckTimer();
      setMarkAllAck(null);
    }
  }, [
    clearMarkAllAckTimer,
    goBack,
    inboxMode,
    isMarkAllAck,
    popup.isMarkingAll,
    popup.markAllSeen,
    popup.notifications.length,
  ]);

  const openRoutine = useCallback(() => {
    setFilter({ kind: "none" });
    navigateSurface({ mode: "categories" });
  }, [navigateSurface]);

  const openAsk = useCallback(
    (origin: UpdatesAskOrigin) => {
      setAskOrigin(origin);
      navigateSurface({ mode: "ask" });
    },
    [navigateSurface],
  );

  const openAskFromCurrentSurface = useCallback(() => {
    if (view.mode === "notable") {
      openAsk({ kind: "notable" });
      return;
    }
    if (view.mode === "categories") {
      openAsk({ kind: "routine" });
      return;
    }
    if (view.mode === "category") {
      const meta = getNotificationCategory(view.filter.category);
      openAsk({
        kind: "category",
        category: view.filter.category,
        label: view.label ?? meta?.label ?? view.filter.category,
        caption: meta?.caption,
        searchQuery: searchInput.trim() || undefined,
      });
      return;
    }
    openAsk({ kind: "summary" });
  }, [openAsk, searchInput, view]);

  const openCategoryDetail = useCallback(
    (
      nextFilter: Extract<NotificationFilter, { kind: "category" }>,
      label?: string,
    ) => {
      setFilter(nextFilter);
      setSearchInput("");
      navigateSurface({ mode: "category", filter: nextFilter, label });
    },
    [navigateSurface],
  );

  const closeButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(SURFACE_ICON_BTN_CLS, "inline-flex items-center justify-center")}
          onClick={onClose}
          aria-label="Close"
        >
          <X className={SURFACE_ICON_GLYPH_CLS} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Close</TooltipContent>
    </Tooltip>
  );

  const shellSize = SHELL_SIZE[shellKind];

  const shellStyle = {
    width: shellSize.width,
    maxWidth: shellSize.maxWidth,
    height: shellSize.height,
    maxHeight: shellSize.maxHeight,
  } satisfies CSSProperties;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        style={shellStyle}
        className={cn(
          "z-[--swivu-bryntum-z-index] flex flex-col gap-0 overflow-hidden border-border/60 bg-background p-0 shadow-2xl",
          "sm:rounded-[2rem]",
          // Radix focuses Content on open / surface swap; suppress the browser outline ring.
          "outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0",
          // Size morph via inline styles; suppress Radix enter zoom/slide that fights the morph.
          "duration-0 data-[state=open]:!animate-none data-[state=closed]:!animate-none",
          "motion-safe:transition-[width,max-width,height,max-height] motion-safe:duration-[320ms] motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
          "motion-reduce:transition-none",
        )}
        showCloseButton={false}
        dialogOverlayClassName="z-[--swivu-bryntum-z-index]"
        onPointerDownOutside={(event) => event.preventDefault()}
        // Escape in the header search clears/collapses the field; it must not close the dialog.
        onEscapeKeyDown={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest("[data-updates-search]")
          ) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogTitle className="sr-only">Updates</DialogTitle>
        <DialogDescription className="sr-only">
          Activity across Swivu: notable updates, notifications, and system operations.
        </DialogDescription>
        <TooltipProvider delayDuration={200}>
          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col overflow-hidden",
              "transition-opacity duration-150 ease-out motion-reduce:transition-none",
              contentVisible ? "opacity-100" : "opacity-0",
            )}
          >
            {!isSummary ? (
              <CommandRail
                view={view}
                askOrigin={askOrigin}
                inboxMode={inboxMode}
                onInboxModeChange={setInboxMode}
                historyRange={historyRange}
                onHistoryRangeChange={setHistoryRange}
                onBack={goBack}
                onOpenAsk={openAskFromCurrentSurface}
                closeButton={closeButton}
                showListChrome={
                  !isMarkAllAck &&
                  (view.mode === "notable" || view.mode === "category")
                }
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                onMarkAllSeen={() => {
                  void handleMarkAllSeen();
                }}
                canMarkAllSeen={!isMarkAllAck && markableCount > 0}
                isMarkingAll={popup.isMarkingAll || isMarkAllAck}
              />
            ) : null}
            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col",
                isSummary
                  ? "overflow-y-auto"
                  : "overflow-hidden bg-muted/40",
              )}
            >
              {isSummary ? (
                popup.isLoading ? (
                  <NotificationSummarySkeleton closeButton={closeButton} />
                ) : (
                  <NotificationSummaryView
                    summary={popup.summary}
                    onOpenNotable={() => navigateSurface({ mode: "notable" })}
                    onOpenRoutine={openRoutine}
                    onOpenAsk={() => openAsk({ kind: "summary" })}
                    closeButton={closeButton}
                    inboxMode={inboxMode}
                    onInboxModeChange={setInboxMode}
                    historyRange={historyRange}
                    onHistoryRangeChange={setHistoryRange}
                    onSeeEarlier={openHistory}
                  />
                )
              ) : null}
              {view.mode === "notable" ? (
                <NotificationNotableView
                  notifications={popup.notifications}
                  context={popup.context}
                  hasNextPage={popup.hasNextPage}
                  isFetchingNextPage={popup.isFetchingNextPage}
                  isLoading={popup.isListPending}
                  onFetchNextPage={() => { void popup.fetchNextPage(); }}
                  onBack={goBack}
                  searchValue={searchInput}
                  onSearchChange={setSearchInput}
                  markAllAck={markAllAck}
                  inboxMode={inboxMode}
                  onSeeEarlier={openHistory}
                />
              ) : null}
              {view.mode === "category" ? (
                <NotificationCategoryView
                  notifications={popup.notifications}
                  context={popup.context}
                  hasNextPage={popup.hasNextPage}
                  isFetchingNextPage={popup.isFetchingNextPage}
                  isLoading={popup.isListPending}
                  onFetchNextPage={() => { void popup.fetchNextPage(); }}
                  onBack={goBack}
                  searchValue={searchInput}
                  onSearchChange={setSearchInput}
                  markAllAck={markAllAck}
                  inboxMode={inboxMode}
                  onSeeEarlier={openHistory}
                  surfaceLabel={
                    view.label
                      ?? getNotificationCategory(view.filter.category)?.label
                      ?? view.filter.category
                  }
                  surfaceClasses={
                    getNotificationCategory(view.filter.category)?.classes
                      ?? { bg: "bg-muted", text: "text-muted-foreground", ring: "ring-muted/20" }
                  }
                  surfaceIcon={getNotificationCategory(view.filter.category)?.icon}
                />
              ) : null}
              {view.mode === "categories" ? (
                <NotificationCategoriesView
                  mappedCounts={popup.mappedRoutineCounts}
                  expanded={shellKind === "expanded"}
                  onBack={goBack}
                  inboxMode={inboxMode}
                  onSeeEarlier={openHistory}
                  onSelectCategory={(nextFilter, label) =>
                    openCategoryDetail(
                      { ...nextFilter, excludeNotable: true },
                      label,
                    )
                  }
                />
              ) : null}
              {view.mode === "ask" ? (
                <NotificationAskView
                  origin={askOrigin}
                  notableTotal={popup.mappedNotableCounts.total}
                  notableUnread={popup.mappedNotableCounts.unread}
                  routineTotal={popup.mappedRoutineCounts.total}
                  routineUnread={popup.mappedRoutineCounts.unread}
                  routineCategories={popup.mappedRoutineCounts.categories}
                  messages={askMessages}
                  sessionId={askSessionId}
                  isLoading={askIsLoading}
                  onMessagesChange={setAskMessages}
                  onSessionIdChange={setAskSessionId}
                  onLoadingChange={setAskIsLoading}
                />
              ) : null}
            </div>
          </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}

function NotificationSummarySkeleton({
  closeButton,
}: {
  closeButton: ReactNode;
}) {
  const shimmer =
    "relative overflow-hidden rounded-xl bg-accent/40 before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-background/60 before:to-transparent";
  return (
    <div className="flex h-full min-h-0 flex-col px-5 pb-5 pt-5 sm:px-6">
      <header className="mb-4 flex shrink-0 items-center justify-between gap-4">
        <div className={cn(shimmer, "h-10 w-48 rounded-2xl")} />
        <div className="flex shrink-0 items-center gap-2">
          <div className={cn(shimmer, "h-9 w-36 rounded-full")} />
          <div className={cn(shimmer, "h-9 w-20 rounded-full")} />
          {closeButton}
        </div>
      </header>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-2 sm:grid-rows-2">
        <div className={cn(shimmer, "h-full min-h-[9rem] rounded-[1.5rem] sm:row-span-2")} />
        <div className={cn(shimmer, "h-full min-h-[9rem] rounded-[1.5rem]")} />
        <div className={cn(shimmer, "h-full min-h-[9rem] rounded-[1.5rem]")} />
      </div>
    </div>
  );
}

function CommandRail({
  view,
  askOrigin,
  inboxMode,
  onInboxModeChange,
  historyRange,
  onHistoryRangeChange,
  onBack,
  onOpenAsk,
  closeButton,
  showListChrome = false,
  searchValue = "",
  onSearchChange,
  onMarkAllSeen,
  canMarkAllSeen = false,
  isMarkingAll = false,
}: {
  view: NotificationSurfaceMode;
  askOrigin: UpdatesAskOrigin;
  inboxMode: UpdatesInboxMode;
  onInboxModeChange: (next: UpdatesInboxMode) => void;
  historyRange: UpdatesHistoryRange;
  onHistoryRangeChange: (next: UpdatesHistoryRange) => void;
  onBack: () => void;
  onOpenAsk: () => void;
  closeButton: ReactNode;
  showListChrome?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onMarkAllSeen?: () => void;
  canMarkAllSeen?: boolean;
  isMarkingAll?: boolean;
}) {
  let surfaceName = "Updates";
  let surfaceCaption: string | null = null;
  if (view.mode === "notable") {
    surfaceName = "Notable";
    surfaceCaption = "Failures, decisions, and unusual changes";
  } else if (view.mode === "category") {
    const categoryMeta = getNotificationCategory(view.filter.category);
    surfaceName = view.label ?? categoryMeta?.label ?? view.filter.category;
    surfaceCaption =
      categoryMeta?.caption ?? "Routine updates in this category";
  } else if (view.mode === "categories") {
    surfaceName = "Routine";
    surfaceCaption = "Browse activity by category";
  } else if (view.mode === "ask") {
    surfaceName = "Ask Swivu";
    surfaceCaption = askSurfaceCaption(askOrigin);
  }

  const showAskButton = view.mode !== "ask";
  const windowChrome =
    view.mode !== "ask" ? (
      <NotificationWindowChrome
        inboxMode={inboxMode}
        onInboxModeChange={onInboxModeChange}
        historyRange={historyRange}
        onHistoryRangeChange={onHistoryRangeChange}
      />
    ) : null;
  const listChrome =
    showListChrome && onSearchChange && onMarkAllSeen ? (
      <NotificationHeaderListChrome
        value={searchValue}
        onChange={onSearchChange}
        onMarkAllSeen={onMarkAllSeen}
        canMarkAllSeen={canMarkAllSeen}
        isMarkingAll={isMarkingAll}
        showMarkAll={canMarkAllSeen || isMarkingAll}
        afterMarkAll={windowChrome}
      />
    ) : null;

  return (
    <header className="shrink-0 border-b border-border/50 bg-background px-5 py-3.5 sm:px-7">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onBack}
              className={cn(
                SURFACE_ICON_BTN_CLS,
                "col-start-1 row-start-1 inline-flex items-center justify-center",
              )}
              aria-label="Back"
            >
              <ChevronLeft className={SURFACE_ICON_GLYPH_CLS} aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Back</TooltipContent>
        </Tooltip>

        <div className="col-start-2 row-start-1 min-w-0">
          <h2 className="truncate text-[1.75rem] font-normal leading-[1.15] tracking-tight text-foreground sm:text-[2rem]">
            {surfaceName}
          </h2>
          {surfaceCaption ? (
            <p className="mt-0.5 truncate text-sm leading-snug text-muted-foreground sm:text-base">
              {surfaceCaption}
            </p>
          ) : null}
        </div>

        <div className="col-start-3 row-start-1 flex items-center justify-end gap-2">
          {listChrome}
          {!listChrome ? windowChrome : null}
          {showAskButton ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onOpenAsk}
                  className={cn(
                    SURFACE_ICON_BTN_CLS,
                    "inline-flex items-center justify-center",
                  )}
                  aria-label="Ask Swivu"
                >
                  <Sparkles className={SURFACE_ICON_GLYPH_CLS} aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Ask Swivu</TooltipContent>
            </Tooltip>
          ) : null}
          {closeButton}
        </div>
      </div>
    </header>
  );
}
